/**
 * TasksTab Component - MED-131 Redesign + MED-475 Filter/Sort/Group Panels
 * Phase-based accordion list view and phase-column board view
 * NO drag-drop, NO stat cards
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Group,
  Button,
  Text,
  TextInput,
  Checkbox,
  CloseButton,
  Loader,
  Center,
  Stack,
  Menu,
  Modal,
  ScrollArea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import Fuse from 'fuse.js';
import { Plus, RefreshCw, Search, X } from 'react-feather';
import { useSubtaskExpansion } from '../../../hooks/useSubtaskExpansion';
import { parseDateOnly } from '../../../utils/dateHelpers';
import type {
  CohortWithDetails,
  TaskWithDetails,
  TaskStatus,
  TaskPhase,
  TaskPhaseRecord,
  TaskFilters,
} from '../../../types';
import { tasksApi } from '../../../services/api/tasks';
import { cohortsApi } from '../../../services/api/cohorts';
import {
  TASK_STATUS_CONFIG,
  TASK_STATUS_OPTIONS,
  calculatePhaseStatus,
} from '../../../config/taskConfig';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { PhaseAccordion, PhaseBoardColumn, AddPhaseModal } from './tasks';
import {
  ButtonToggle,
  AvatarFilterBar,
  FilterPanel,
  SortPanel,
} from '../../../components/common';
import type { AvatarFilterItem, ActiveFilterRow, FilterFieldConfig } from '../../../components/common';
import type { SortState } from '../../../utils/sortTasks';
import { sortItems } from '../../../utils/sortTasks';

// Due date filter options
const DUE_FILTER_OPTIONS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'due_today', label: 'Due Today' },
  { value: 'due_this_week', label: 'Due This Week' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'no_due_date', label: 'No Due Date' },
];

// Sort field configs for cohort tasks
const COHORT_SORT_FIELDS = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'status', label: 'Status' },
  { value: 'title', label: 'Title' },
];

// Quick filter configs for FilterPanel
const COHORT_QUICK_FILTERS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_today', label: 'Due Today' },
  { key: 'due_this_week', label: 'Due This Week' },
];

// Status sort order for custom ordering
const STATUS_ORDER: Record<string, number> = {
  in_progress: 0,
  not_started: 1,
  blocked: 2,
  complete: 3,
  cancelled: 4,
};

/**
 * Check which due filter categories a task matches
 */
function getTaskDueCategories(task: TaskWithDetails): string[] {
  const categories: string[] = [];

  // MED-874: A parent task whose subtasks are overdue acts as an Overdue anchor
  // row, even if the parent itself is not overdue (or has no due date). Backed
  // by the has_overdue_subtasks boolean computed server-side (MED-768).
  if (task.has_overdue_subtasks) {
    categories.push('overdue');
  }

  if (!task.due_date) {
    categories.push('no_due_date');
    return categories;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // MED-1265: parse as a calendar date — new Date() on the API's UTC-midnight
  // timestamp lands on the previous day for viewers west of UTC.
  const dueDate = parseDateOnly(task.due_date)!;

  if (
    task.status !== 'complete' &&
    task.status !== 'cancelled' &&
    dueDate < today &&
    !categories.includes('overdue')
  ) {
    categories.push('overdue');
  }
  if (dueDate.getTime() === today.getTime()) {
    categories.push('due_today');
  }

  // Due this week: Sunday to Saturday
  const dayOfWeek = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  if (dueDate >= sunday && dueDate <= saturday) {
    categories.push('due_this_week');
  }

  // Upcoming: after today
  if (dueDate > today) {
    categories.push('upcoming');
  }

  return categories;
}

interface TasksTabProps {
  cohort: CohortWithDetails;
  onOpenTaskModal?: (task?: TaskWithDetails, defaultPhase?: TaskPhase) => void;
  /** Callback when task status changes (for refreshing status readiness in header) */
  onTaskStatusChanged?: () => void;
  isActive?: boolean;
  /** MED-770: Deep link — auto-open this task's detail drawer on mount */
  initialTaskId?: string | null;
  /** MED-770: Callback to sync task drawer state with URL */
  onTaskDrawerChange?: (taskId: string | null) => void;
}

export function TasksTab({ cohort, onOpenTaskModal, onTaskStatusChanged, isActive, initialTaskId, onTaskDrawerChange }: TasksTabProps) {
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [phases, setPhases] = useState<TaskPhaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => {
    const saved = localStorage.getItem('tasksViewMode');
    return (saved === 'board' || saved === 'list') ? saved : 'list';
  });
  const [filters, setFilters] = useState<TaskFilters>({});
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [selectedDueFilters, setSelectedDueFilters] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // New: Sort state (grouping is always by phase)
  const [sortState, setSortState] = useState<SortState | null>(null);

  // Track explicitly added filter rows
  const [addedFilterKeys, setAddedFilterKeys] = useState<Set<string>>(new Set());

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('tasksViewMode', viewMode);
  }, [viewMode]);

  // Task detail drawer state
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Regenerate confirmation modal state
  const [regenerateModalOpened, setRegenerateModalOpened] = useState(false);
  const [taskCounts, setTaskCounts] = useState<{ total: number; templateTasks: number; manualTasks: number } | null>(null);

  // Add Phase modal state
  const [addPhaseModalOpened, setAddPhaseModalOpened] = useState(false);

  // MED-479: Subtask expansion
  const {
    expandedTaskIds,
    subtaskCache,
    loadingTaskIds,
    toggleTask,
    expandTasks,
    collapseTasks,
    invalidateCache,
    updateSubtaskInCache,
  } = useSubtaskExpansion();

  // MED-868: Flatten the subtask cache into a {subtaskId: status} map so the
  // TaskDetailDrawer can render checkbox state from the same source the inline
  // dropdown reads from. This keeps both surfaces in sync without a refetch
  // when the user toggles subtasks from either side while the drawer is open.
  const subtaskStatusMap = useMemo<Record<string, TaskStatus>>(() => {
    const map: Record<string, TaskStatus> = {};
    for (const subtasks of Object.values(subtaskCache)) {
      for (const subtask of subtasks) {
        map[subtask.id] = subtask.status;
      }
    }
    return map;
  }, [subtaskCache]);

  // Warning modal state for completing parent tasks with incomplete subtasks
  const [completeWarningOpened, setCompleteWarningOpened] = useState(false);
  const [pendingCompleteTaskId, setPendingCompleteTaskId] = useState<string | null>(null);
  const [pendingCompleteCurrentStatus, setPendingCompleteCurrentStatus] = useState<TaskStatus | null>(null);
  const [alsoCompleteSubtasks, setAlsoCompleteSubtasks] = useState(false);

  // MED-874: Auto-expand state for the Overdue quick filter.
  // - When the filter transitions OFF→ON, queue an auto-expand pass and clear
  //   the user-collapsed tracker so newly-revealed anchors expand fresh (AC12).
  // - During an active filter session, manual collapses are recorded so the
  //   auto-expand pass does NOT fight the user on subsequent renders (AC11).
  // - Auto-expand fires only on the transition into the filter, not on every
  //   data change (AC10).
  const isOverdueFilterActive = selectedDueFilters.includes('overdue');
  const autoExpandStateRef = useRef({
    filterWasActive: isOverdueFilterActive,
    needsAutoExpand: isOverdueFilterActive,
    userCollapsed: new Set<string>(),
  });

  const handleToggleExpand = useCallback((taskId: string) => {
    const state = autoExpandStateRef.current;
    if (state.filterWasActive && expandedTaskIds.has(taskId)) {
      state.userCollapsed.add(taskId);
    }
    toggleTask(taskId, cohort.id);
  }, [toggleTask, cohort.id, expandedTaskIds]);

  // MED-874: Drive the auto-expand pass. Detects the OFF→ON transition for the
  // Overdue filter (or initial mount with the filter active) and expands every
  // parent in the loaded task set whose has_overdue_subtasks flag is true.
  // Runs once per filter activation — subsequent task-data changes do NOT
  // re-expand (AC10).
  useEffect(() => {
    const state = autoExpandStateRef.current;
    if (isOverdueFilterActive && !state.filterWasActive) {
      state.needsAutoExpand = true;
      state.userCollapsed = new Set();
    }
    state.filterWasActive = isOverdueFilterActive;

    if (state.needsAutoExpand && tasks.length > 0) {
      const parentsToExpand = tasks
        .filter((t) => t.has_overdue_subtasks && !state.userCollapsed.has(t.id))
        .map((t) => ({ id: t.id, cohortId: cohort.id, subtaskCount: t.subtask_count }));
      state.needsAutoExpand = false;
      if (parentsToExpand.length > 0) {
        expandTasks(parentsToExpand);
      }
    }
  }, [isOverdueFilterActive, tasks, expandTasks, cohort.id]);

  // MED-769: Per-phase expand all / collapse all handlers
  const handlePhaseExpandAll = useCallback((phaseTasks: TaskWithDetails[]) => {
    expandTasks(
      phaseTasks.map((t) => ({ id: t.id, cohortId: cohort.id, subtaskCount: t.subtask_count })),
    );
  }, [expandTasks, cohort.id]);

  const handlePhaseCollapseAll = useCallback((phaseTasks: TaskWithDetails[]) => {
    collapseTasks(phaseTasks.map((t) => t.id));
  }, [collapseTasks]);

  const isPhaseAllExpanded = useCallback((phaseTasks: TaskWithDetails[]) => {
    const parentTasks = phaseTasks.filter((t) => t.subtask_count > 0);
    return parentTasks.length > 0 && parentTasks.every((t) => expandedTaskIds.has(t.id));
  }, [expandedTaskIds]);

  // Open task detail drawer
  const openTaskDrawer = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setDrawerOpened(true);
    onTaskDrawerChange?.(taskId);
  }, [onTaskDrawerChange]);

  // Close task detail drawer
  const closeTaskDrawer = useCallback(() => {
    setDrawerOpened(false);
    setSelectedTaskId(null);
    onTaskDrawerChange?.(null);
  }, [onTaskDrawerChange]);

  // MED-770: Auto-open drawer from deep link URL
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (initialTaskId && !deepLinkHandledRef.current && !loading && isActive) {
      deepLinkHandledRef.current = true;
      openTaskDrawer(initialTaskId);
    }
  }, [initialTaskId, loading, isActive, openTaskDrawer]);

  // Fetch phases applicable to this cohort based on its type and modalities
  const fetchPhases = useCallback(async () => {
    try {
      const phasesData = await cohortsApi.getPhases(cohort.id);
      // Already filtered and sorted by backend, but ensure sort order
      const sortedPhases = phasesData.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setPhases(sortedPhases);
    } catch (error) {
      console.error('Failed to fetch phases:', error);
    }
  }, [cohort.id]);

  // Fetch ALL tasks (unfiltered — status/phase filtering is done client-side
  // so phase status badges always reflect the full dataset per MED-444)
  const fetchTasks = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const tasksData = await tasksApi.getTasksForCohort(cohort.id);
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load tasks',
        color: 'red',
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [cohort.id]);

  // Only fetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchPhases();
      fetchTasks();
    }
  }, [isActive, fetchPhases, fetchTasks]);

  // Get ordered phase slugs from dynamic phases
  const phaseOrder = useMemo(() => {
    return phases.map(p => p.slug as TaskPhase);
  }, [phases]);

  // Build phase options for filter dropdown
  const phaseFilterOptions = useMemo(() => {
    return phases.map(p => ({
      value: p.slug,
      label: p.name,
    }));
  }, [phases]);

  // Derive unique assignees from unfiltered task data
  const uniqueAssignees = useMemo((): AvatarFilterItem[] => {
    const map = new Map<string, AvatarFilterItem>();
    tasks.forEach((task) => {
      if (task.assignee_id && task.assignee_name && !map.has(task.assignee_id)) {
        map.set(task.assignee_id, {
          id: task.assignee_id,
          name: task.assignee_name,
          colorKey: task.assignee_id,
        });
      }
    });
    return Array.from(map.values());
  }, [tasks]);

  const hasUnassignedTasks = useMemo(() => tasks.some((t) => !t.assignee_id), [tasks]);

  // ── Filter pipeline ────────────────────────────────────────────────────

  // 1. Client-side status/phase filter
  const statusPhaseFilteredTasks = useMemo(() => {
    let result = tasks;
    if (filters.status && filters.status.length > 0) {
      result = result.filter((t) => filters.status!.includes(t.status));
    }
    if (filters.phase && filters.phase.length > 0) {
      result = result.filter((t) => t.phase && filters.phase!.includes(t.phase));
    }
    return result;
  }, [tasks, filters]);

  // 2. Client-side due date filter
  const dueFilteredTasks = useMemo(() => {
    if (selectedDueFilters.length === 0) return statusPhaseFilteredTasks;
    return statusPhaseFilteredTasks.filter((task) => {
      const categories = getTaskDueCategories(task);
      return selectedDueFilters.some((f) => categories.includes(f));
    });
  }, [statusPhaseFilteredTasks, selectedDueFilters]);

  // 3. Client-side assignee filter
  const assigneeFilteredTasks = useMemo(() => {
    if (selectedAssigneeIds.length === 0) return dueFilteredTasks;
    return dueFilteredTasks.filter((task) => {
      if (selectedAssigneeIds.includes('unassigned') && !task.assignee_id) return true;
      return task.assignee_id ? selectedAssigneeIds.includes(task.assignee_id) : false;
    });
  }, [dueFilteredTasks, selectedAssigneeIds]);

  // 4. Fuzzy search with Fuse.js (operates on assignee-filtered tasks)
  const fuse = useMemo(() => new Fuse(assigneeFilteredTasks, {
    keys: ['title'],
    threshold: 0.4,
    ignoreLocation: true,
  }), [assigneeFilteredTasks]);

  const searchedTasks = useMemo(() => {
    if (!debouncedSearch.trim()) return assigneeFilteredTasks;
    return fuse.search(debouncedSearch).map((result) => result.item);
  }, [fuse, debouncedSearch, assigneeFilteredTasks]);

  const filteredTasks = searchedTasks;

  // 5. Client-side sort (applied to filtered tasks)
  const sortedFilteredTasks = useMemo(() => {
    return sortItems(filteredTasks, sortState, {
      due_date: (t) => t.due_date || '',
      status: (t) => STATUS_ORDER[t.status] ?? 99,
      title: (t) => t.title.toLowerCase(),
    });
  }, [filteredTasks, sortState]);

  // ── Filter panel props ─────────────────────────────────────────────────

  const filterFieldConfigs = useMemo((): FilterFieldConfig[] => [
    { key: 'status', label: 'Status', type: 'multiselect', options: TASK_STATUS_OPTIONS },
    { key: 'phase', label: 'Phase', type: 'multiselect', options: phaseFilterOptions },
    { key: 'due_date', label: 'Due Date', type: 'multiselect', options: DUE_FILTER_OPTIONS },
  ], [phaseFilterOptions]);

  const activeFilters = useMemo((): ActiveFilterRow[] => {
    const rows: ActiveFilterRow[] = [];
    if ((filters.status?.length ?? 0) > 0 || addedFilterKeys.has('status')) {
      rows.push({ key: 'status', values: (filters.status as string[]) || [] });
    }
    if ((filters.phase?.length ?? 0) > 0 || addedFilterKeys.has('phase')) {
      rows.push({ key: 'phase', values: (filters.phase as string[]) || [] });
    }
    if (selectedDueFilters.length > 0 || addedFilterKeys.has('due_date')) {
      rows.push({ key: 'due_date', values: selectedDueFilters });
    }
    return rows;
  }, [filters, selectedDueFilters, addedFilterKeys]);

  // Quick filter state derived from selectedDueFilters
  const activeQuickFilters = useMemo(() => {
    return selectedDueFilters.filter((f) => ['overdue', 'due_today', 'due_this_week'].includes(f));
  }, [selectedDueFilters]);

  // Active filter count for clear button
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if ((filters.status?.length ?? 0) > 0) count++;
    if ((filters.phase?.length ?? 0) > 0) count++;
    if (selectedDueFilters.length > 0) count++;
    if (selectedAssigneeIds.length > 0) count++;
    if (debouncedSearch.trim()) count++;
    if (sortState !== null) count++;
    return count;
  }, [filters, selectedDueFilters, selectedAssigneeIds, debouncedSearch, sortState]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setSelectedDueFilters([]);
    setSelectedAssigneeIds([]);
    setSearchValue('');
    setSortState(null);
    setAddedFilterKeys(new Set());
  }, []);

  // FilterPanel handlers
  const handleFilterAdd = useCallback((key: string) => {
    setAddedFilterKeys((prev) => new Set(prev).add(key));
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === 'status') setFilters((prev) => ({ ...prev, status: values as TaskStatus[] }));
    else if (key === 'phase') setFilters((prev) => ({ ...prev, phase: values as TaskPhase[] }));
    else if (key === 'due_date') setSelectedDueFilters(values);
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    if (key === 'status') setFilters((prev) => ({ ...prev, status: undefined }));
    else if (key === 'phase') setFilters((prev) => ({ ...prev, phase: undefined }));
    else if (key === 'due_date') setSelectedDueFilters([]);
    setAddedFilterKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleFilterClearAll = useCallback(() => {
    setFilters({});
    setSelectedDueFilters([]);
    setAddedFilterKeys(new Set());
  }, []);

  const handleQuickFilterToggle = useCallback((key: string) => {
    setSelectedDueFilters((prev) => {
      if (prev.includes(key)) {
        return prev.filter((f) => f !== key);
      }
      return [...prev, key];
    });
  }, []);

  const handleSortChange = useCallback((sort: SortState | null) => {
    setSortState(sort);
  }, []);

  // ── Phase grouping (existing logic, for phase-specific views) ──────────

  // Group ALL tasks by phase (unfiltered — for status badges and progress counts)
  const allTasksByPhase = useMemo(() => {
    const grouped = new Map<TaskPhase, TaskWithDetails[]>();
    phaseOrder.forEach((phase) => {
      grouped.set(phase, []);
    });
    tasks.forEach((task) => {
      if (task.phase) {
        const existing = grouped.get(task.phase) || [];
        grouped.set(task.phase, [...existing, task]);
      }
    });
    return grouped;
  }, [tasks, phaseOrder]);

  // Group FILTERED+SORTED tasks by phase (for display)
  const tasksByPhase = useMemo(() => {
    const grouped = new Map<TaskPhase, TaskWithDetails[]>();
    phaseOrder.forEach((phase) => {
      grouped.set(phase, []);
    });
    sortedFilteredTasks.forEach((task) => {
      if (task.phase) {
        const existing = grouped.get(task.phase) || [];
        grouped.set(task.phase, [...existing, task]);
      }
    });
    return grouped;
  }, [sortedFilteredTasks, phaseOrder]);

  // Grouping is always by phase in cohort detail view

  // Calculate which phases should be expanded by default based on phase status
  const defaultExpandedPhases = useMemo(() => {
    const expanded = new Set<TaskPhase>();

    const phaseStatuses = phaseOrder.map((phase) => ({
      phase,
      status: calculatePhaseStatus(allTasksByPhase.get(phase) || []),
    }));

    const inProgressPhases = phaseStatuses.filter((p) => p.status === 'in_progress');

    if (inProgressPhases.length > 0) {
      inProgressPhases.forEach((p) => expanded.add(p.phase));
    } else {
      const firstIncomplete = phaseStatuses.find((p) => p.status === 'not_started');
      if (firstIncomplete) {
        expanded.add(firstIncomplete.phase);
      } else {
        if (phaseOrder.length > 0) {
          expanded.add(phaseOrder[phaseOrder.length - 1]);
        }
      }
    }

    return expanded;
  }, [phaseOrder, allTasksByPhase]);

  // Controlled accordion expand/collapse state
  const [expandedPhases, setExpandedPhases] = useState<Set<TaskPhase>>(new Set());

  // Track whether the user has manually toggled a phase accordion
  const userHasInteractedRef = useRef(false);

  // Ref to save pre-search/filter accordion state for restore
  const savedExpandedRef = useRef<Set<TaskPhase> | null>(null);

  // Detect whether any search/filter is active
  const isFilterActive = useMemo(() => {
    return (
      debouncedSearch.trim() !== '' ||
      selectedAssigneeIds.length > 0 ||
      selectedDueFilters.length > 0 ||
      (filters.status?.length ?? 0) > 0 ||
      (filters.phase?.length ?? 0) > 0
    );
  }, [debouncedSearch, selectedAssigneeIds, selectedDueFilters, filters]);

  // Reset user interaction flag when tab becomes active so auto-expand re-evaluates
  useEffect(() => {
    if (isActive) {
      userHasInteractedRef.current = false;
    }
  }, [isActive]);

  // Sync expanded phases from default calculation whenever it changes,
  // unless the user has manually interacted with the accordion
  useEffect(() => {
    if (defaultExpandedPhases.size > 0 && !userHasInteractedRef.current && !isFilterActive) {
      setExpandedPhases(new Set(defaultExpandedPhases));
    }
  }, [defaultExpandedPhases, isFilterActive]);

  // Auto-expand/collapse phases based on search/filter results
  useEffect(() => {
    if (isFilterActive) {
      if (savedExpandedRef.current === null) {
        savedExpandedRef.current = new Set(expandedPhases);
      }
      const phasesWithResults = new Set<TaskPhase>();
      tasksByPhase.forEach((phaseTasks, phase) => {
        if (phaseTasks.length > 0) {
          phasesWithResults.add(phase);
        }
      });
      setExpandedPhases(phasesWithResults);
    } else if (savedExpandedRef.current !== null) {
      setExpandedPhases(new Set(defaultExpandedPhases));
      savedExpandedRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterActive, tasksByPhase, defaultExpandedPhases]);

  // Toggle a single accordion phase
  const handlePhaseToggle = useCallback((phase: TaskPhase) => {
    userHasInteractedRef.current = true;
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phase)) {
        next.delete(phase);
      } else {
        next.add(phase);
      }
      return next;
    });
  }, []);


  // Handle toggle task completion (actual execution)
  const executeToggleComplete = async (taskId: string, currentStatus: TaskStatus, completeSubtasks?: boolean) => {
    const newStatus: TaskStatus = currentStatus === 'complete' ? 'not_started' : 'complete';

    // Optimistically update UI (parent tasks in state + subtasks in cache)
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus, ...(completeSubtasks && newStatus === 'complete' ? { subtasks_completed: t.subtask_count } : {}) } : t))
    );
    updateSubtaskInCache(taskId, { status: newStatus });

    // If completing subtasks too, optimistically mark all cached subtasks as complete
    if (completeSubtasks && newStatus === 'complete' && subtaskCache[taskId]) {
      for (const subtask of subtaskCache[taskId]) {
        if (subtask.status !== 'complete') {
          updateSubtaskInCache(subtask.id, { status: 'complete' });
        }
      }
    }

    // If this is a subtask, also update the parent task's subtasks_completed count
    // so the header counter and incomplete-subtasks check reflect the new state immediately
    let parentTaskId: string | null = null;
    for (const [parentId, subtasks] of Object.entries(subtaskCache)) {
      if (subtasks.some((s) => s.id === taskId)) {
        parentTaskId = parentId;
        break;
      }
    }
    if (parentTaskId) {
      const delta = newStatus === 'complete' ? 1 : -1;
      setTasks((prev) =>
        prev.map((t) =>
          t.id === parentTaskId
            ? { ...t, subtasks_completed: Number(t.subtasks_completed) + delta }
            : t,
        ),
      );
    }

    try {
      await tasksApi.updateTaskStatus(cohort.id, taskId, newStatus, completeSubtasks);
      notifications.show({
        title: 'Task Updated',
        message: completeSubtasks
          ? 'Task and subtasks marked as Complete'
          : `Task marked as ${TASK_STATUS_CONFIG[newStatus].label}`,
        color: 'green',
      });
      onTaskStatusChanged?.();
    } catch (error) {
      console.error('Failed to update task status:', error);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: currentStatus } : t))
      );
      updateSubtaskInCache(taskId, { status: currentStatus });
      // Reverse the parent count update on failure
      if (parentTaskId) {
        const delta = newStatus === 'complete' ? -1 : 1;
        setTasks((prev) =>
          prev.map((t) =>
            t.id === parentTaskId
              ? { ...t, subtasks_completed: Number(t.subtasks_completed) + delta }
              : t,
          ),
        );
      }
      notifications.show({
        title: 'Error',
        message: 'Failed to update task',
        color: 'red',
      });
    }
  };

  // MED-868: Drawer notifies us after a subtask toggle persists. We mirror the
  // change into the shared subtask cache (so the inline dropdown updates) and
  // adjust the parent's subtasks_completed count (so the "Incomplete Subtasks"
  // warning check at handleToggleComplete reads live state instead of a stale
  // server-side count).
  const handleDrawerSubtaskToggled = useCallback(
    (subtaskId: string, newStatus: TaskStatus, parentTaskId: string) => {
      updateSubtaskInCache(subtaskId, { status: newStatus });
      const delta = newStatus === 'complete' ? 1 : -1;
      setTasks((prev) =>
        prev.map((t) =>
          t.id === parentTaskId
            ? { ...t, subtasks_completed: Number(t.subtasks_completed) + delta }
            : t,
        ),
      );
    },
    [updateSubtaskInCache],
  );

  // Handle toggle with warning modal for parent tasks with incomplete subtasks
  const handleToggleComplete = (taskId: string, currentStatus: TaskStatus) => {
    if (currentStatus !== 'complete') {
      const task = tasks.find((t) => t.id === taskId);
      if (task && Number(task.subtask_count) > 0 && Number(task.subtasks_completed) < Number(task.subtask_count)) {
        setPendingCompleteTaskId(taskId);
        setPendingCompleteCurrentStatus(currentStatus);
        setCompleteWarningOpened(true);
        return;
      }
    }
    executeToggleComplete(taskId, currentStatus);
  };

  // Handle task click - open drawer
  const handleTaskClick = (task: TaskWithDetails) => {
    openTaskDrawer(task.id);
  };

  // Handle edit task
  const handleEditTask = (task: TaskWithDetails) => {
    onOpenTaskModal?.(task);
  };

  // Handle delete task
  const handleDeleteTask = async (taskId: string) => {
    try {
      await tasksApi.bulkOperation(cohort.id, {
        task_ids: [taskId],
        action: 'delete',
      });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      notifications.show({
        title: 'Task Deleted',
        message: 'Task has been deleted',
        color: 'green',
      });
      onTaskStatusChanged?.();
    } catch (error) {
      console.error('Failed to delete task:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete task',
        color: 'red',
      });
    }
  };

  // Handle add task within phase
  const handleAddTask = (phase: TaskPhaseRecord | TaskPhase) => {
    const phaseValue = typeof phase === 'string' ? phase : (phase.slug as TaskPhase);
    onOpenTaskModal?.(undefined, phaseValue);
  };

  // Handle duplicate task
  const handleDuplicateTask = async (taskId: string) => {
    try {
      const duplicatedTask = await tasksApi.duplicateTask(cohort.id, taskId);
      setTasks((prev) => [...prev, duplicatedTask]);
      notifications.show({
        title: 'Task Duplicated',
        message: 'Task has been duplicated successfully',
        color: 'green',
      });
      openTaskDrawer(duplicatedTask.id);
    } catch (error) {
      console.error('Failed to duplicate task:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to duplicate task',
        color: 'red',
      });
    }
  };

  // Check for existing tasks and show modal if needed
  const handleRegenerateClick = async () => {
    try {
      const counts = await cohortsApi.getTaskCount(cohort.id);
      setTaskCounts(counts);

      if (counts.total > 0) {
        setRegenerateModalOpened(true);
      } else {
        await executeRegenerate('skip_existing');
      }
    } catch (error) {
      console.error('Failed to check task count:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to check existing tasks',
        color: 'red',
      });
    }
  };

  // Execute regenerate with selected mode
  const executeRegenerate = async (mode: 'skip_existing' | 'replace_template_tasks') => {
    try {
      setRegenerating(true);
      setRegenerateModalOpened(false);
      const result = await cohortsApi.regenerateTasks(cohort.id, mode);
      await fetchTasks();
      onTaskStatusChanged?.();

      if (mode === 'replace_template_tasks') {
        notifications.show({
          title: 'Tasks Replaced',
          message: `Deleted ${result.tasksDeleted} tasks, created ${result.tasksGenerated} new tasks`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Tasks Generated',
          message: result.tasksGenerated > 0
            ? `${result.tasksGenerated} new tasks created from templates`
            : 'No new tasks to create (all templates already have tasks)',
          color: result.tasksGenerated > 0 ? 'green' : 'blue',
        });
      }
    } catch (error) {
      console.error('Failed to regenerate tasks:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to regenerate tasks',
        color: 'red',
      });
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Toolbar: filters left, actions right */}
      <Group justify="space-between" wrap="wrap">
        {/* Left side: View toggle, search, panels, avatars, clear */}
        <Group gap="sm" wrap="wrap">
          <ButtonToggle
            options={[
              { value: 'board', label: 'Board' },
              { value: 'list', label: 'List' },
            ]}
            value={viewMode}
            onChange={(v) => setViewMode(v)}
          />

          <TextInput
            placeholder="Search tasks..."
            leftSection={<Search size={16} />}
            rightSection={searchValue ? (
              <CloseButton size="sm" onClick={() => setSearchValue('')} />
            ) : undefined}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            w={280}
          />

          {/* Filter Panel */}
          <FilterPanel
            quickFilters={COHORT_QUICK_FILTERS}
            activeQuickFilters={activeQuickFilters}
            onQuickFilterToggle={handleQuickFilterToggle}
            filterFields={filterFieldConfigs}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={handleFilterClearAll}
          />

          {/* Sort Panel */}
          <SortPanel
            sortFields={COHORT_SORT_FIELDS}
            activeSort={sortState}
            onSortChange={handleSortChange}
          />

          {uniqueAssignees.length > 0 && (
            <AvatarFilterBar
              items={uniqueAssignees}
              selectedIds={selectedAssigneeIds}
              onToggle={(id) => {
                setSelectedAssigneeIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
              maxVisible={5}
              size={28}
              showUnassigned={hasUnassignedTasks}
            />
          )}

          {activeFilterCount > 0 && (
            <Button
              variant="subtle"
              color="gray"
              leftSection={<X size={14} />}
              onClick={clearAllFilters}
            >
              Clear ({activeFilterCount})
            </Button>
          )}

          {/* Zero results indicator */}
          {isFilterActive && filteredTasks.length === 0 && (
            <Text size="sm" c="dimmed" fw={500}>
              0 Results
            </Text>
          )}
        </Group>

        {/* Right side: Action buttons */}
        <Group gap="sm">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="outline"
                color="gray"
                bg="white"
                style={{ borderColor: '#E8E6E3' }}
                leftSection={<Plus size={16} />}
                loading={regenerating}
              >
                Add Task
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<Plus size={14} />}
                onClick={() => onOpenTaskModal?.()}
              >
                Create New Task
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<RefreshCw size={14} />}
                onClick={handleRegenerateClick}
              >
                Regenerate from Templates
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Button
            leftSection={<Plus size={16} />}
            onClick={() => setAddPhaseModalOpened(true)}
          >
            Add Phase
          </Button>
        </Group>
      </Group>

      {/* Main content — always grouped by phase */}
      {viewMode === 'list' ? (
        <Stack gap="xs">
          {phaseOrder.map((phase) => {
            const phaseTasks = tasksByPhase.get(phase) || [];
            const phaseRecord = phases.find(p => p.slug === phase);

            return (
              <PhaseAccordion
                key={phase}
                phase={phaseRecord || phase}
                tasks={phaseTasks}
                allTasks={allTasksByPhase.get(phase) || []}
                isFilterActive={isFilterActive}
                expanded={expandedPhases.has(phase)}
                onToggle={() => handlePhaseToggle(phase)}
                onToggleComplete={handleToggleComplete}
                onTaskClick={handleTaskClick}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onDuplicateTask={handleDuplicateTask}
                onAddTask={handleAddTask}
                expandedTaskIds={expandedTaskIds}
                loadingTaskIds={loadingTaskIds}
                subtaskCache={subtaskCache}
                onToggleExpand={handleToggleExpand}
                onExpandAllTasks={() => handlePhaseExpandAll(phaseTasks)}
                onCollapseAllTasks={() => handlePhaseCollapseAll(phaseTasks)}
                allTasksExpanded={isPhaseAllExpanded(phaseTasks)}
                cohortId={cohort.id}
              />
            );
          })}

          {/* Show message when all phases are empty */}
          {(phaseOrder.length === 0 || phaseOrder.every((phase) => (tasksByPhase.get(phase) || []).length === 0)) && (
            <Text c="dimmed" ta="center" py="xl">
              {debouncedSearch.trim()
                ? `No tasks matching "${debouncedSearch}"`
                : 'No tasks yet. Click "Add Task" to create one.'}
            </Text>
          )}
        </Stack>
      ) : (
        <ScrollArea type="auto" offsetScrollbars>
          <Group gap="md" align="flex-start" wrap="nowrap" pb="md">
            {phaseOrder.map((phase) => {
              const phaseTasks = tasksByPhase.get(phase) || [];
              const phaseRecord = phases.find(p => p.slug === phase);

              return (
                <PhaseBoardColumn
                  key={phase}
                  phase={phaseRecord || phase}
                  tasks={phaseTasks}
                  allTasks={allTasksByPhase.get(phase) || []}
                  isFilterActive={isFilterActive}
                  onToggleComplete={handleToggleComplete}
                  onTaskClick={handleTaskClick}
                  onAddTask={handleAddTask}
                  cohortId={cohort.id}
                />
              );
            })}
          </Group>
        </ScrollArea>
      )}

      {/* Task detail drawer */}
      <TaskDetailDrawer
        cohortId={cohort.id}
        taskId={selectedTaskId}
        opened={drawerOpened}
        onClose={closeTaskDrawer}
        externalSubtaskStatusMap={subtaskStatusMap}
        onSubtaskToggled={handleDrawerSubtaskToggled}
        onUpdated={(updatedTask) => {
          if (updatedTask) {
            setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t)));
            fetchTasks(false);
            // MED-479: Invalidate subtask cache for this task or its parent
            if (updatedTask.parent_task_id) {
              invalidateCache(updatedTask.parent_task_id);
            } else {
              invalidateCache(updatedTask.id);
            }
          } else {
            fetchTasks();
          }
          onTaskStatusChanged?.();
        }}
        onEditTask={(task) => {
          closeTaskDrawer();
          onOpenTaskModal?.(task);
        }}
      />

      {/* Regenerate confirmation modal */}
      <Modal
        opened={regenerateModalOpened}
        onClose={() => setRegenerateModalOpened(false)}
        title="Regenerate Tasks"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            This cohort already has {taskCounts?.total || 0} tasks
            {taskCounts?.templateTasks ? ` (${taskCounts.templateTasks} from templates, ${taskCounts.manualTasks} manual)` : ''}.
          </Text>
          <Text size="sm" c="dimmed">
            How would you like to proceed?
          </Text>

          <Stack gap="xs">
            <Button
              variant="light"
              fullWidth
              onClick={() => executeRegenerate('skip_existing')}
            >
              Add Missing Tasks Only
              <Text size="xs" c="dimmed" ml="xs">
                (keeps existing tasks, adds new templates)
              </Text>
            </Button>

            <Button
              variant="light"
              color="red"
              fullWidth
              onClick={() => executeRegenerate('replace_template_tasks')}
            >
              Replace All Template Tasks
              <Text size="xs" c="dimmed" ml="xs">
                (deletes {taskCounts?.templateTasks || 0} template tasks, keeps {taskCounts?.manualTasks || 0} manual)
              </Text>
            </Button>

            <Button
              variant="default"
              fullWidth
              onClick={() => setRegenerateModalOpened(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Modal>

      {/* Add Phase modal */}
      <AddPhaseModal
        opened={addPhaseModalOpened}
        onClose={() => setAddPhaseModalOpened(false)}
        onCreated={() => {
          fetchPhases();
          fetchTasks();
        }}
      />

      {/* MED-479: Warning modal for completing parent with incomplete subtasks */}
      <Modal
        opened={completeWarningOpened}
        onClose={() => {
          setCompleteWarningOpened(false);
          setPendingCompleteTaskId(null);
          setPendingCompleteCurrentStatus(null);
          setAlsoCompleteSubtasks(false);
        }}
        title="Incomplete Subtasks"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This task has incomplete subtasks. Are you sure you want to complete this task?
          </Text>
          <Checkbox
            label="Also complete all incomplete subtasks"
            checked={alsoCompleteSubtasks}
            onChange={(event) => setAlsoCompleteSubtasks(event.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setCompleteWarningOpened(false);
                setPendingCompleteTaskId(null);
                setPendingCompleteCurrentStatus(null);
                setAlsoCompleteSubtasks(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                if (pendingCompleteTaskId && pendingCompleteCurrentStatus) {
                  executeToggleComplete(pendingCompleteTaskId, pendingCompleteCurrentStatus, alsoCompleteSubtasks);
                }
                setCompleteWarningOpened(false);
                setPendingCompleteTaskId(null);
                setPendingCompleteCurrentStatus(null);
                setAlsoCompleteSubtasks(false);
              }}
            >
              Complete Task
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

